import React from 'react';
import { motion } from 'framer-motion';
import { Eyebrow, Card } from './UI';
import { Star, ShieldCheck, ThumbsUp, MapPin, BadgeCheck } from 'lucide-react';
// Trustpilot-style filled star (acid green square with white star)
const StarBlock = ({
  size = 18
}: {
  size?: number;
}) => <span className="inline-flex items-center justify-center bg-acid-green" style={{
  width: size + 4,
  height: size + 4
}} data-id="element-1387" data-mp="3buohgwv2f9ow">
    <Star size={size} className="text-black" fill="currentColor" strokeWidth={0} data-id="element-1388" data-mp="3fd9ad0o82w4" />
  </span>;
const StarRow = ({
  count = 5,
  size = 16
}: {
  count?: number;
  size?: number;
}) => <div className="flex gap-[2px]" data-id="element-1389" data-mp="3pu62r295k4ti">
    {Array.from({
    length: 5
  }).map((_, i) => <span key={i} className={`inline-flex items-center justify-center ${i < count ? 'bg-acid-green' : 'bg-dark-3'}`} style={{
    width: size + 4,
    height: size + 4
  }} data-id="element-1390" data-mp="3urprwx7e2208" data-mpi={i}>
        <Star size={size} className={i < count ? 'text-black' : 'text-dark-3'} fill="currentColor" strokeWidth={0} data-id="element-1391" data-mp="r6a7kmzjn1ow" />
      </span>)}
  </div>;
// Avatar with initials
const Avatar = ({
  name,
  hue
}: {
  name: string;
  hue: number;
}) => {
  const initials = name.split(' ').filter(p => p && !p.includes('|') && !p.includes('(')).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  return <div className="w-11 h-11 shrink-0 flex items-center justify-center font-bebas text-[16px] text-white border border-white/10" style={{
    background: `linear-gradient(135deg, hsl(${hue}, 30%, 22%) 0%, hsl(${hue}, 25%, 14%) 100%)`
  }} data-id="element-1392" data-mp="2cyrmksbe2ory">
      {initials}
    </div>;
};
export const SocialProof = () => {
  const reviews = [{
    title: "Toughest mask I've ever put on my face",
    body: "11 years of airsoft. Probably 15 different masks. This is the only one I've ever reviewed because it's the only one that actually earned it. Took a point-blank hit at 390 FPS. Didn't even flinch. The lenses are clearer than my $200 Oakleys. My team ordered three more.",
    name: 'Marcus R.',
    location: 'Texas, USA',
    meta: 'Verified Airsoft Competitor',
    date: 'Reviewed 14 days ago',
    helpful: 142,
    hue: 90
  }, {
    title: 'Won best costume — 200 people asked me where I got this',
    body: 'Dragon Con 2024. I had literally two hundred people stop me to ask about the mask. I printed cards pointing them to the website. Won best in show. The gold lens matched my armor like it was custom built for it. This thing is a statement piece.',
    name: 'Jordan K.',
    location: 'Atlanta, GA',
    meta: 'Cosplay Artist',
    date: 'Reviewed 3 weeks ago',
    helpful: 89,
    hue: 280
  }, {
    title: "My buddy's F-250 ran over it. I'm dead serious.",
    body: 'Parking lot incident at our field. Full-size truck, loaded, rolled the front tire over the shell. We thought it was destroyed. Picked it up — no cracks, no structural damage. Lenses were fine. We posted the photo. 47,000 impressions. The mask sold itself.',
    name: 'Derek W.',
    location: 'Nashville, TN',
    meta: 'Content Creator',
    date: 'Reviewed 1 month ago',
    helpful: 318,
    hue: 30
  }, {
    title: 'Finally built like the guys who wear it',
    body: '20-year Army veteran. I have zero patience for gear that looks tactical and performs like a toy. The BLACKOUT UNIT is the real thing. The construction, the weight distribution, the materials — this was built by someone who actually understands field equipment. Respect. I bought two more for my sons.',
    name: 'SSG D. Morrison',
    location: 'Arizona, USA',
    meta: 'US Army Veteran (Ret.)',
    date: 'Reviewed 5 weeks ago',
    helpful: 256,
    hue: 200
  }, {
    title: 'The lens swap system is pure genius',
    body: 'I switch from clear to smoke to gold depending on conditions. Takes me 20 seconds without tools. No other mask on the market does this. The anti-fog coating actually works at full sprint — that alone puts it in a completely different class.',
    name: 'Tyler M.',
    location: 'Nevada, USA',
    meta: 'Competitive Airsoft Player',
    date: 'Reviewed 6 weeks ago',
    helpful: 74,
    hue: 160
  }, {
    title: 'Four of us on the squad — everyone demanded one',
    body: 'Bought the squad bundle for our paintball team. The other team literally paused mid-round to ask us about the masks. We dominated. We looked elite. The lens swap meant we all matched colors in under two minutes. Worth every dollar — times four.',
    name: 'Brandon K.',
    location: 'Colorado, USA',
    meta: 'Paintball Team Captain',
    date: 'Reviewed 2 months ago',
    helpful: 121,
    hue: 60
  }];
  // Star distribution (Trustpilot style)
  const distribution = [{
    stars: 5,
    pct: 92
  }, {
    stars: 4,
    pct: 6
  }, {
    stars: 3,
    pct: 1
  }, {
    stars: 2,
    pct: 0.5
  }, {
    stars: 1,
    pct: 0.5
  }];
  return <section id="intel" className="bg-[#0a0a0a] pt-[40px] pb-[100px]" data-id="element-1393" data-mp="3qex8wukda3h9">
      <div className="container mx-auto px-6 md:px-12" data-id="element-1394" data-mp="3qh9kmpzj7hlv">
        <div className="text-center mb-12" data-id="element-1395" data-mp="3lh5635i6owya">
          <Eyebrow data-id="element-1396" data-mp="3lig7wzs2jumr">FIELD INTEL</Eyebrow>
          <h2 className="text-5xl md:text-[72px] leading-[0.9] mb-4" data-id="element-1397" data-mp="2xppyax7n1nfn">
            OPERATORS <span className="text-acid-green" data-id="element-1398" data-mp="2sroega2e1k34">DON'T LIE.</span>
          </h2>
          <p className="font-barlow text-lg text-text-body max-w-2xl mx-auto" data-id="element-1399" data-mp="2strfdp52wxmm">
            Verified reviews from operators who deployed the BLACKOUT UNIT
            Ultimate Mask in the field.
          </p>
        </div>

        {/* Trustpilot-style summary panel */}
        <div className="max-w-[1100px] mx-auto border border-dark-3 bg-dark-1 mb-12" data-id="element-1400" data-mp="lzdv9jij72au">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr] divide-y md:divide-y-0 md:divide-x divide-dark-3" data-id="element-1401" data-mp="hybxl749iyaz">
            {/* Score block */}
            <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-1402" data-mp="3fsyfrsiof3qt">
              <div className="flex items-center gap-2 mb-3" data-id="element-1403" data-mp="3furmt8x7bpzc">
                <ShieldCheck size={16} className="text-acid-green" data-id="element-1404" data-mp="3ktcn18cge9ns" />
                <span className="font-barlow text-[10px] text-acid-green tracking-[2px] uppercase" data-id="element-1405" data-mp="3ou4x23t5gil4">
                  Independently Verified
                </span>
              </div>
              <div className="font-bebas text-[64px] text-white leading-none mb-3" data-id="element-1406" data-mp="2x2grq18sgwbs">
                4.9
                <span className="text-text-dimmed text-[28px]" data-id="element-1407" data-mp="314aldpkkh02g"> / 5.0</span>
              </div>
              <StarRow count={5} size={20} data-id="element-1408" data-mp="35uin3l403mf7" />
              <div className="font-barlow text-sm text-text-body tracking-wide mt-3" data-id="element-1409" data-mp="35vkamp27e1dt">
                Based on{' '}
                <span className="text-white font-bold" data-id="element-1410" data-mp="29cmehikk55r0">9,273 reviews</span>
              </div>
              <div className="flex items-center gap-2 mt-4 text-text-dimmed" data-id="element-1411" data-mp="2dcn31w1dexbs">
                <BadgeCheck size={14} className="text-acid-green" data-id="element-1412" data-mp="25tnkttpnbbu0" />
                <span className="font-inter text-[11px]" data-id="element-1413" data-mp="20w3y05evtqqg">
                  TrustScore — Excellent
                </span>
              </div>
            </div>

            {/* Distribution + secondary stats */}
            <div className="p-8" data-id="element-1414" data-mp="1rvyzkkro22of">
              <div className="font-barlow text-[10px] text-acid-green tracking-[2px] uppercase mb-4" data-id="element-1415" data-mp="1runtufenukuo">
                ━━ RATING DISTRIBUTION ━━
              </div>
              <div className="space-y-2 mb-6" data-id="element-1416" data-mp="1i54c5d2oox1e">
                {distribution.map(d => <div key={d.stars} className="flex items-center gap-3" data-id="element-1417" data-mp="1d5rs1037642i">
                    <div className="font-barlow text-[11px] text-text-dimmed w-12" data-id="element-1418" data-mp="194obsch8m60r">
                      {d.stars}-star
                    </div>
                    <div className="flex-grow h-2 bg-dark-3 overflow-hidden" data-id="element-1419" data-mp="144jvonk8rdb2">
                      <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: `${d.pct}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut'
                  }} className="h-full bg-acid-green" data-id="element-1420" data-mp="z5yv2n12urm3" />
                    </div>
                    <div className="font-barlow text-[11px] text-white w-10 text-right" data-id="element-1421" data-mp="293yi11wx19fn">
                      {d.pct}%
                    </div>
                  </div>)}
              </div>
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-dark-3 text-center" data-id="element-1422" data-mp="1zcu8ihq0egs7">
                <div data-id="element-1423" data-mp="1ldkplaaf58kj">
                  <div className="font-bebas text-2xl text-white" data-id="element-1424" data-mp="1lf5kj10natix">97%</div>
                  <div className="font-barlow text-[9px] text-text-dimmed tracking-[2px] uppercase" data-id="element-1425" data-mp="1qd72tjq8yotc">
                    Recommend
                  </div>
                </div>
                <div data-id="element-1426" data-mp="11s2h9tbtrskb">
                  <div className="font-bebas text-2xl text-white" data-id="element-1427" data-mp="11suawylv3g7y">50,287</div>
                  <div className="font-barlow text-[9px] text-text-dimmed tracking-[2px] uppercase" data-id="element-1428" data-mp="1gp1ubwpg4dm6">
                    Deployed
                  </div>
                </div>
                <div data-id="element-1429" data-mp="1bsbbj1m9b3x2">
                  <div className="font-bebas text-2xl text-white" data-id="element-1430" data-mp="1ppq7kp70m9y0">100%</div>
                  <div className="font-barlow text-[9px] text-text-dimmed tracking-[2px] uppercase" data-id="element-1431" data-mp="1pnx2hec1vxs4">
                    Verified
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1200px] mx-auto" data-id="element-1432" data-mp="1ui1am0x1ybus">
          {reviews.map((rev, i) => <Card key={i} className="p-6 flex flex-col" data-id="element-1433" data-mp="24ew60ay13w4u" data-mpi={i}>
              {/* Header — avatar, name, verified */}
              <div className="flex items-start gap-3 mb-4" data-id="element-1434" data-mp="1a0y7ihfbfoof">
                <Avatar name={rev.name} hue={rev.hue} data-id="element-1435" data-mp="1a2j1o5xpx5lr" />
                <div className="flex-grow min-w-0" data-id="element-1436" data-mp="15a7zewfi97q1">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-1437" data-mp="158evhowa11lq">
                    <span className="font-barlow font-bold text-[14px] text-white truncate" data-id="element-1438" data-mp="10c6julhafqjb">
                      {rev.name}
                    </span>
                    <BadgeCheck size={14} className="text-acid-green shrink-0" data-id="element-1439" data-mp="1sztzdm3pbqz3" />
                  </div>
                  <div className="flex items-center gap-1 font-inter text-[11px] text-text-dimmed" data-id="element-1440" data-mp="1nywidcufh2z6">
                    <MapPin size={10} data-id="element-1441" data-mp="1j3zcufwcxbr1" />
                    <span className="truncate" data-id="element-1442" data-mp="1f0u9yc8d781v">{rev.location}</span>
                  </div>
                </div>
              </div>

              {/* Stars + date */}
              <div className="flex items-center justify-between mb-4" data-id="element-1443" data-mp="7kegcom8o4w3">
                <StarRow count={5} size={12} data-id="element-1444" data-mp="3jktlykj8w4s" />
                <div className="font-inter text-[10px] text-text-dimmed" data-id="element-1445" data-mp="2f8ra2gke2obv">
                  {rev.date}
                </div>
              </div>

              {/* Title */}
              <h4 className="font-bebas text-[18px] text-white mb-2 leading-tight" data-id="element-1446" data-mp="2j8065s2wiyal">
                {rev.title}
              </h4>

              {/* Body */}
              <p className="font-inter text-[13px] text-text-body leading-relaxed mb-5 flex-grow" data-id="element-1447" data-mp="2t0xony7w8n97">
                {rev.body}
              </p>

              {/* Footer — meta + helpful */}
              <div className="flex items-center justify-between pt-4 border-t border-dark-3" data-id="element-1448" data-mp="32xag0pfyup0s">
                <div className="flex items-center gap-1.5 font-barlow text-[10px] text-acid-green tracking-[1.5px] uppercase" data-id="element-1449" data-mp="36zfbzutvzxql">
                  <ShieldCheck size={11} data-id="element-1450" data-mp="3by8deovwfyae" />
                  <span data-id="element-1451" data-mp="1clnuk95bg9kl">{rev.meta}</span>
                </div>
                <div className="flex items-center gap-1 font-inter text-[10px] text-text-dimmed" data-id="element-1452" data-mp="1cnh0fm83uprj">
                  <ThumbsUp size={10} data-id="element-1453" data-mp="1hkzc3kdibj38" />
                  <span data-id="element-1454" data-mp="2kvd5u3xl0ylh">{rev.helpful}</span>
                </div>
              </div>
            </Card>)}
        </div>
      </div>
    </section>;
};